/*
 * Convert to Infographic panel.
 *
 * Turns the current selection (a table, a text shape) — or, absent a
 * selection, the whole current slide — into an AI-generated infographic
 * inserted at the end of the deck. Same one-shot-action shape as Fix
 * Alignment's card: describe what you want (optional), press the button,
 * watch the two steps, see the result.
 */

import { useCallback, useEffect, useRef, useState, type ChangeEvent } from "react";
import { ModuleHeader } from "../../../../../shared/components/ModuleHeader";
import { MAX_USER_DESCRIPTION_CHARS } from "../services/infographicApi";
import { useConvertInfographic, type InfographicPhase } from "../hooks/useConvertInfographic";
import styles from "./InfographicPanel.module.css";

interface InfographicPanelProps {
  readonly onBack?: () => void;
  /** Pre-fills the description field — e.g. Get Insights routing here with
   *  its bullets as steering text for the AI. */
  readonly initialDescription?: string;
  /** Starts the conversion immediately on mount instead of waiting for the
   *  user to press "Create Infographic" — set alongside `initialDescription`
   *  when another panel is routing the user straight into a run. */
  readonly autoRun?: boolean;
}

const DESCRIPTION_PLACEHOLDER =
  "e.g., Show as a timeline with milestones, or Create a SWOT analysis, or leave empty to let AI decide";

/** The steps the progress list walks, in order. */
const PHASES: ReadonlyArray<{ readonly phase: InfographicPhase; readonly label: string }> = [
  { phase: "analyzing", label: "Analyzing content" },
  { phase: "composing", label: "Composing layout" },
];

/** Where a step sits relative to the one currently running. */
type StepState = "done" | "active" | "waiting";

function stepStateAt(index: number, currentIndex: number): StepState {
  if (index < currentIndex) return "done";
  return index === currentIndex ? "active" : "waiting";
}

const STEP_CLASS: Record<StepState, string | undefined> = {
  done: styles.stepDone,
  active: styles.stepActive,
  waiting: styles.stepWaiting,
};

function StepMark({ state }: Readonly<{ state: StepState }>) {
  if (state === "done") return <>✓</>;
  if (state === "active") return <span className={styles.spinner} />;
  return <>○</>;
}

/** The progress list — completed steps tick, the current one spins, the rest wait. */
function ProgressSteps({ current }: Readonly<{ current: InfographicPhase }>) {
  const currentIndex = PHASES.findIndex((step) => step.phase === current);
  return (
    <ol className={styles.steps} aria-label="Convert to infographic progress">
      {PHASES.map((step, index) => {
        const state = stepStateAt(index, currentIndex);
        return (
          <li
            key={step.phase}
            className={`${styles.step} ${STEP_CLASS[state]}`}
            aria-current={state === "active" || undefined}
          >
            <span className={styles.stepMark} aria-hidden="true">
              <StepMark state={state} />
            </span>
            <span>{step.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

function describeLayout(infographicType: string | null, replacedSelection: boolean): string {
  const layout = infographicType
    ? ` — AI picked a ${infographicType.replace(/_/g, " ").trim().toLowerCase()} layout`
    : "";
  const placement = replacedSelection
    ? "replacing the selected area (inserted as the next slide)"
    : "as a new slide";
  return `Infographic inserted${layout} in brand colors, ${placement}.`;
}

export function InfographicPanel({
  onBack,
  initialDescription,
  autoRun,
}: Readonly<InfographicPanelProps>) {
  const { state, run, cancel } = useConvertInfographic();
  const [description, setDescription] = useState(initialDescription ?? "");
  const isRunning = state.status === "running";

  const handleDescriptionChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value.slice(0, MAX_USER_DESCRIPTION_CHARS));
  }, []);

  const handleCreateClick = useCallback(() => {
    void run(description);
  }, [run, description]);

  // Fires once per mount — a ref (not a dep-gated effect) so it survives
  // description edits and StrictMode's dev-only double-invoke without
  // starting a second run.
  const hasAutoRunRef = useRef(false);
  useEffect(() => {
    if (!autoRun || !initialDescription || hasAutoRunRef.current) return;
    hasAutoRunRef.current = true;
    void run(initialDescription);
  }, []);

  return (
    <div className={styles.panel}>
      <ModuleHeader title="Convert to Infographic" subtitle="Deckorate" onBack={onBack} />
      <div className={styles.body}>
        <p className={styles.desc}>
          AI will analyze your content and create the best infographic layout automatically — from
          the selected table or text, or the current slide if nothing's selected.
        </p>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="infographic-description">
            Describe your infographic (optional)
          </label>
          <textarea
            id="infographic-description"
            className={styles.textarea}
            rows={3}
            maxLength={MAX_USER_DESCRIPTION_CHARS}
            placeholder={DESCRIPTION_PLACEHOLDER}
            value={description}
            onChange={handleDescriptionChange}
            disabled={isRunning}
            data-testid="infographic-description-input"
          />
        </div>

        <button
          type="button"
          className={styles.runBtn}
          onClick={handleCreateClick}
          disabled={isRunning}
          data-testid="infographic-create"
        >
          {isRunning ? "Creating infographic…" : "Create Infographic"}
        </button>

        {isRunning && (
          <>
            <ProgressSteps current={state.phase} />
            <button type="button" className={styles.cancelBtn} onClick={cancel} data-testid="infographic-cancel">
              Cancel
            </button>
          </>
        )}

        {state.status === "cancelled" && (
          <div className={`${styles.statusBox} ${styles.statusInfo}`}>
            <span className={styles.statusText}>Cancelled — nothing was changed.</span>
          </div>
        )}

        {state.status === "done" && (
          <div className={`${styles.statusBox} ${styles.statusSuccess}`}>
            <span className={styles.statusText}>
              {describeLayout(state.infographicType, state.replacedSelection)}
            </span>
          </div>
        )}

        {state.status === "error" && (
          <div className={`${styles.statusBox} ${styles.statusError}`}>
            <span className={styles.statusLabel}>Failed:</span>
            <span className={styles.statusText}>{state.message}</span>
          </div>
        )}
      </div>
    </div>
  );
}
